import React, { useState, useEffect, useMemo } from "react";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import { toastSuccess } from "../../utils/toastUtils";
import Loader from "../../Layouts/Loader/Loader";
import "./AssignUserToApplication.css";
import ButtonActionIcon from "../../components/BuilderComponents/ButtonActionIcon";
import { debounce } from "../../commonFunctions/arrayFunc";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import Button from "../../components/FormComponents/Button";

const AssignUserToApplication = ({ appId, orgId }) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);

  const fetchAssignedUsers = async () => {
    try {
      setIsTableLoading(true);
      const { data } = await axiosInstance.get(`/user-service/app/${appId}`);
      setSelectedUsers(data || []);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsTableLoading(false);
    }
  };

  // Restored: This function now makes an API call to assign the user
  const assignUser = async (userObj) => {
    try {
      const payload = { designation: "Associate" };
      const { message } = await axiosInstance.post(`/org/${orgId}/user/${userObj.id}/app/${appId}`, payload);

      // Update the local state directly after a successful API call
      setSelectedUsers((prev) => [...prev, { ...userObj, designation: "Associate" }]);
      toastSuccess(message);
      return true;
    } catch (error) {
      handleApiError(error);
      return false;
    }
  };

  // Restored: This function now makes an API call to remove the user
  const removeUser = async (id) => {
    try {
      const { message } = await axiosInstance.delete(`/org/${orgId}/user/${id}/app/${appId}`);

      // Update the local state directly after a successful API call
      setSelectedUsers((prev) => prev.filter((user) => user.id !== id));
      toastSuccess(message);
      return true;
    } catch (error) {
      handleApiError(error);
      return false;
    }
  };

  const fetchSearchResults = async (term) => {
    try {
      const url = term ? `/user-services/users?search=${term}&limit=20` : `/user-services/users?limit=10`;

      const { data } = await axiosInstance.get(url);

      return data.filter((user) => !selectedUsers.some((sel) => sel.id === user.id));
    } catch (error) {
      handleApiError(error);
      return [];
    }
  };

  useEffect(() => {
    if (appId) fetchAssignedUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  return (
    <>
      {isTableLoading ? (
        <div className="assign_loader_overlay">
          <Loader />
        </div>
      ) : (
        <UserAssignmentUI
          assignedUsers={selectedUsers}
          onAssign={assignUser}
          onRemove={removeUser}
          fetchSearchResults={fetchSearchResults}
        />
      )}
    </>
  );
};

const UserAssignmentUI = ({ assignedUsers, onAssign, onRemove, fetchSearchResults }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [assignedSearchTerm, setAssignedSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [confirmationState, setConfirmationState] = useState({
    isOpen: false,
    action: null,
    user: null
  });

  const getAvatarLetter = (user) => {
    if (user.name) {
      const nameParts = user.name.split(" ").filter((part) => part.length > 0);
      if (nameParts.length === 1) {
        return nameParts[0].charAt(0).toUpperCase();
      } else if (nameParts.length > 1) {
        const firstNameInitial = nameParts[0].charAt(0).toUpperCase();
        const lastNameInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
        return `${firstNameInitial}${lastNameInitial}`;
      }
    }
    if (user.email && user.email.length > 0) {
      return user.email.charAt(0).toUpperCase();
    }
    return "?";
  };

  const debouncedFetch = useMemo(() => {
    return debounce(async (val) => {
      setIsSearching(true);
      const result = await fetchSearchResults(val);
      setSearchResults(result);
      setIsSearching(false);
    }, 300);
  }, [fetchSearchResults]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedFetch(value);
  };

  const closeConfirmationModal = () => {
    setConfirmationState({
      isOpen: false,
      action: null,
      user: null
    });
  };

  const openConfirmationModal = (action, user) => {
    setConfirmationState({
      isOpen: true,
      action,
      user
    });
  };

  const handleAssignUser = (user) => {
    openConfirmationModal("assign", user);
  };

  const handleRemoveUser = (user) => {
    openConfirmationModal("remove", user);
  };

  const confirmUserAction = async () => {
    const { action, user } = confirmationState;
    if (!user || !action) return;

    let isSuccess = false;

    if (action === "assign") {
      isSuccess = await onAssign(user);
      if (isSuccess) {
        setSearchResults((prev) => prev.filter((u) => u.id !== user.id));
      }
    }

    if (action === "remove") {
      isSuccess = await onRemove(user.id);
    }

    if (isSuccess) {
      closeConfirmationModal();
    }
  };

  const getConfirmationTitle = () => {
    if (confirmationState.action === "assign") return "Confirm User Assignment";
    if (confirmationState.action === "remove") return "Confirm User Removal";
    return "Confirm Action";
  };

  const getConfirmationMessage = () => {
    const { action, user } = confirmationState;
    if (!user) return "";

    const userName = user.name || user.email || "this user";

    if (action === "assign") {
      return `Are you sure you want to add ${userName} ?`;
    }

    if (action === "remove") {
      return `Are you sure you want to remove ${userName} ?`;
    }

    return "";
  };

  useEffect(() => {
    if (searchTerm.trim() === "") {
      (async () => {
        setIsSearching(true);
        const result = await fetchSearchResults("");
        setSearchResults(result);
        setIsSearching(false);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="assign_wrapper">
        {/* Left Side - Search and Assign */}
        <div className="assign_left">
          <label className="assign_label" style={{ height: "31px" }}>
            Search Users
          </label>
          <div className="assign_search_container">
            <input
              type="text"
              placeholder="Type to search users..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="assign_input"
            />
          </div>
          {isSearching ? (
            <div className="assign_loader_container">
              <Loader />
            </div>
          ) : (
            <div className="assign_result_list">
              {searchResults.length > 0 ? (
                searchResults.map((user) => (
                  <div key={user.id} className="assign_user_item">
                    <div className="assign_user_details">
                      <div className="assign_user_avatar">{getAvatarLetter(user)}</div>
                      <div>
                        <div className="assign_name_text">{user.name || "Unnamed User"}</div>
                        <div className="assign_email_text">{user.email || "No email"}</div>
                      </div>
                    </div>
                    <ButtonActionIcon type="add" size="sm" onClick={() => handleAssignUser(user)} />
                  </div>
                ))
              ) : (
                <div className="assign_no_data">No results found</div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Assigned Users */}
        <div className="assign_right">
          <label className="assign_label">
            <span>Assigned Users</span>
            <span className="assign_count">Assigned: {assignedUsers.length}</span>
          </label>
          <div className="assign_search_container">
            <input
              type="text"
              placeholder="Search assigned users..."
              value={assignedSearchTerm}
              onChange={(e) => setAssignedSearchTerm(e.target.value)}
              className="assign_input"
            />
          </div>
          {assignedUsers.length > 0 ? (
            <div className="assign_selected_list">
              {assignedUsers
                .filter((user) => {
                  const name = user.name?.toLowerCase() || "";
                  const email = user.email?.toLowerCase() || "";
                  const term = assignedSearchTerm.toLowerCase();
                  return name.includes(term) || email.includes(term);
                })
                .map((user) => (
                  <div key={user.id} className="assign_user_chip">
                    <div className="assign_user_details">
                      <div className="assign_user_avatar">{getAvatarLetter(user)}</div>
                      <div>
                        <div className="assign_name_text">{user.name || "Unnamed User"}</div>
                        <div className="assign_email_text">{user.email || "No email"}</div>
                      </div>
                    </div>
                    <div className="assign_actions">
                      <ButtonActionIcon type="delete" size="sm" onClick={() => handleRemoveUser(user)} />
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="assign_no_data">No users assigned</div>
          )}
        </div>
      </div>
      <BuilderModal
        isOpen={confirmationState.isOpen}
        onClose={closeConfirmationModal}
        modalName={getConfirmationTitle()}
        showCloseBtn={false}
        customStyleClsName="assign_confirmation_modal"
        backdropStyle={{ zIndex: 10001 }}
      >
        <div className="assign_confirmation_content">
          <p className="assign_confirmation_text">{getConfirmationMessage()}</p>
          <div className="assign_confirmation_actions">
            <Button variant="plain" className="confirmation_popup_buttons" onClick={closeConfirmationModal}>
              Cancel
            </Button>
            <Button className="confirmation_popup_buttons" onClick={confirmUserAction}>
              Confirm
            </Button>
          </div>
        </div>
      </BuilderModal>
    </>
  );
};

export { UserAssignmentUI };
export default AssignUserToApplication;
